import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import {
  getCollectPublicationYear,
  getProjectDefaultPubDateFormat,
  getFieldOptions,
  getStepErrors,
  getStepFields,
} from "../../common/selectors";
import { anyNotBlank, isBlank, notBlank } from "../../common/general";
import { FIELD_BLURRED, FIELD_CHANGED } from "../fileDetailsActions";
import * as steps from "common/steps";
import { firstError, hasError } from "common/validation";

import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";

import DatePicker from "../../general-components/DatePicker";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  dateFormat: {
    marginRight: 8,
    marginTop: 10,
    [theme.breakpoints.up("sm")]: {
      marginTop: 0,
    },
  },
}));

const PublicationDate = ({ xs = true, sm = true, md = true }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isSm = useMediaQuery(theme => theme.breakpoints.up("sm"));
  const isMd = useMediaQuery(theme => theme.breakpoints.up("md"));

  const errors = useSelector(getStepErrors(steps.FILE_DETAILS));
  const fields = useSelector(getStepFields(steps.FILE_DETAILS));
  const fieldOptions = useSelector(getFieldOptions);
  const collectPublicationYear = useSelector(getCollectPublicationYear);
  const defaultPubDateFormat = useSelector(getProjectDefaultPubDateFormat);

  const dispatch = useDispatch();
  const onBlur = (field, value) => () =>
    dispatch({ type: FIELD_BLURRED, field, value });
  const onFieldChange = path => event => {
    if (notBlank(event)) {
      const value = notBlank(event.target) ? event.target.value : event;
      dispatch({ type: FIELD_CHANGED, path, value });
    }
  };

  const toPublicationValues = (dateField, dateTime) => {
    const year = dateTime.year.toString();
    const date =
      dateField === "publicationDate"
        ? dateTime.toFormat("yyyy-MM-dd")
        : `${year}-01-01`;

    return { date, year };
  };

  const onDateChange = dateField => event => {
    if (notBlank(event)) {
      const value = notBlank(event.target) ? event.target.value : event;
      const { date, year } = toPublicationValues(dateField, value);

      onFieldChange(["publicationDate"])(date);
      onFieldChange(["publicationYear"])(year);
    }
  };

  // Picking from the calendar doesn't blur the input, so the validation message stays
  // even when a value has populated the input
  // We can validate on accept to clear (or refresh) the validation error.
  const onDateAccept = dateField => dateTime => {
    if (notBlank(dateTime)) {
      const { date, year } = toPublicationValues(dateField, dateTime);
      const value = dateField === "publicationDate" ? date : year;

      dispatch({ type: FIELD_BLURRED, field: dateField, value });
    }
  };

  const [show, setShow] = useState(true);
  useEffect(() => {
    let visible = true;

    if (
      anyNotBlank([
        fieldOptions["publication_year"],
        fieldOptions["publication_date"],
      ])
    ) {
      switch (defaultPubDateFormat) {
        case "year":
          visible = !fieldOptions["publication_year"]?.hidden;
          break;
        case "date":
          visible = !fieldOptions["publication_date"]?.hidden;
          break;
        default:
          visible =
            !fieldOptions["publication_year"]?.hidden ||
            !fieldOptions["publication_date"]?.hidden;
      }
    }

    setShow(visible || collectPublicationYear);
  }, [defaultPubDateFormat, fieldOptions, collectPublicationYear]);

  const [fullDate, setFullDate] = useState(true);
  useEffect(() => {
    if (notBlank(defaultPubDateFormat)) {
      setFullDate(defaultPubDateFormat === "date");
    }
  }, [defaultPubDateFormat]);

  if (!show) {
    return null;
  }

  const dateField = fullDate ? "publicationDate" : "publicationYear";
  const dateValue = fullDate
    ? fields.publicationDate || ""
    : fields.publicationYear
    ? `${fields.publicationYear}-01-01`
    : "";

  return (
    <Grid
      container
      item
      xs={12}
      sm={isBlank(defaultPubDateFormat) ? 12 : 6}
      md={6}
    >
      {isBlank(defaultPubDateFormat) && (
        <Grid item>
          <FormControl className={classes.dateFormat}>
            <InputLabel
              id="publication-date-format-label"
              htmlFor="publication-date-format"
            >
              Date Format
            </InputLabel>
            <Select
              value={fullDate ? "pd" : "py"}
              onChange={event => {
                // Reset both fields on field change
                setFullDate(event.target.value === "pd");
                onFieldChange(["publicationDate"])({ target: { value: null } });
                onFieldChange(["publicationYear"])({ target: { value: null } });
              }}
              inputProps={{
                name: "content-full-date-select",
                id: "publication-date-format",
              }}
            >
              <MenuItem value={"py"} aria-label="Publication Year">
                {fieldOptions?.publication_year?.fieldLabel
                  ? fieldOptions.publication_year.fieldLabel
                  : isMd
                  ? "Publication Year"
                  : "Pub. Year"}
              </MenuItem>
              <MenuItem value={"pd"} aria-label="Publication Date">
                {fieldOptions?.publication_date?.fieldLabel
                  ? fieldOptions.publication_date.fieldLabel
                  : isMd
                  ? "Publication Date"
                  : "Pub. Date"}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}

      <DatePicker
        fullDate={fullDate}
        id={fullDate ? undefined : "publication-year"}
        required={
          collectPublicationYear ||
          (fullDate
            ? fieldOptions?.publication_date?.required
            : fieldOptions?.publication_year?.required)
        }
        xs={xs}
        sm={sm}
        md={md}
        marginTop={isSm ? 0 : "10px"}
        label={
          fullDate
            ? fieldOptions?.publication_date?.fieldLabel ||
              t("file_details.publication_date", "Publication Date")
            : fieldOptions?.publication_year?.fieldLabel ||
              t("file_details.publication_year", "Year published")
        }
        placeholder={fullDate ? "mm/dd/yyyy" : "yyyy"}
        value={dateValue}
        onBlur={onBlur(dateField, fields[dateField])}
        onChange={onDateChange(dateField)}
        onAccept={onDateAccept(dateField)}
        error={hasError(errors, dateField)}
        helperText={t(firstError(errors, dateField))}
      />
    </Grid>
  );
};

export default PublicationDate;
